import { Button, Col, Container, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import Paginations from "../pagination";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Breadcrumbs from "../breadcrumbs";
import { useSearchParams } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import html2pdf from "html2pdf.js";
import DeleteConfirmationModal from "../delete-confirmation-modal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAccess } from "../../contexts/AccessContext";
import { isAdminRole } from "../../shared/utils";

const PingingDataTable = ({
  title,
  apiEndpoint,
  defaultValue = "web_all",
  showVideoColumns = false,
  showPlayVideo = false,
  type = "ping",
  pingType = "video",
  toolId,
}) => {
  const blocked = [3,4,5,6];
  const [searchParams] = useSearchParams();
  const [valueFromUrl, setValueFromUrl] = useState(searchParams.get("value") || defaultValue);

  const getTitleSuffix = () => {
    const suffixMap = {
      good_video: "Good Videos",
      bad_video: "Bad Videos",
      video_all: "All Videos",
      downloaded: "Downloaded Videos",
      not_downloaded: '"Non-Downloaded" Videos',
      replaced: "Broken Videos Replaced with Back-Up Videos",
      good_web: "Good Web Links",
      bad_web: "Bad Web Links",
      web_all: "All Web Links",
    };
    return suffixMap[valueFromUrl] || "";
  };

  const getDisplayTitle = () => {
    const suffix = getTitleSuffix();
    if (!suffix) return title;
    
    const pageType = type === "assign" ? "Assignment Pages" : "Lesson Pages";
    return `Pinging Report - ${pageType}: ${suffix}`;
  };

  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    page_size: 0,
    total_page: 1,
    total_records: 0,
  });
  const [page, setPage] = useState(1);
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [buttonsLoading, setButtonsLoading] = useState({
    disabled: false, // global disable to prevent concurrent operations
    copy: false,
    csv: false,
    excel: false,
    pdf: false,
    print: false,
  });

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Note modal states
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const [itemToNote, setItemToNote] = useState(null);

  const debounceTimeoutRef = useRef(null);
  const { hasEdit } = useAccess();
  const isAdmin = useMemo(() => isAdminRole(), []);

  // Check if showing replaced records
  const isReplacedView = valueFromUrl === "replaced";

  const handlePlayVideo = (downloaded_url_id) => {
    // let secureVideoUrl = videoUrl;
    // if (secureVideoUrl && secureVideoUrl.includes("youtube.com")) {
    //   secureVideoUrl = secureVideoUrl.replace("http://", "https://");
    let path = '';

    if (pingType === 'video' && type === 'assign') {
      path = 'assignment/';
    } else if (pingType === 'video' && type === 'ping') {
      path = 'course/';
    }
    // }
    let videoUrl = `${process.env.REACT_APP_S3PATH}${path}${downloaded_url_id}.mp4`
    setCurrentVideoUrl(videoUrl);
    setShowVideoModal(true);
  };

  // Debounce search text
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    debounceTimeoutRef.current = timer;

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [searchText]);

  const loadItems = useCallback(() => {
    setShowSpinner(true);
    setSelectedIds([]);

    const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
    const params = new URLSearchParams({
      page,
      per_page: 20,
      value: valueFromUrl,
      search: debouncedSearchText?.trim() ?? "",
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    axios
      .get(`${url}?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        const data = response.data.data || [];
        setTableData(data);
        setPaginationData(
          response.data.page_data ?? {
            current_page: 1,
            page_size: 0,
            total_page: 1,
            total_records: 0,
          },
        );
      })
      .catch((error) => {
        const { message } = error?.response?.data;
        console.error("Error fetching data:", error);
        toast.error(message || "Error fetching data");
      })
      .finally(() => {
        setShowSpinner(false);
      });
  }, [page, valueFromUrl, debouncedSearchText, apiEndpoint, sortBy, sortOrder]);

  // Reset page and search when URL value changes
  useEffect(() => {
    const newValue = searchParams.get("value") || defaultValue;
    if (newValue !== valueFromUrl) {
      setValueFromUrl(newValue);
      setPage(1);
      setSearchText("");
      setDebouncedSearchText("");
      setSelectedIds([]);
    }
  }, [searchParams, valueFromUrl, defaultValue]);

  // Load data when page or filters change
  useEffect(() => {
    // if (page) {
      loadItems();
    // }
  }, [page, sortBy, sortOrder, debouncedSearchText, valueFromUrl]);


  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      } else {
        setSortOrder("desc");
        return column;
      }
    });

    setPage(1);
  };



  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(tableData.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleClear = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    setSearchText("");
    setDebouncedSearchText("");
    setPage(1);
    setSelectedIds([]);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Add these functions after formatDate function
  const getTableHeaders = () => {
    const headers = [];
    if (showVideoColumns) headers.push("ID");
    headers.push("Course ID", "Course Name", "Page Title");
    if (showVideoColumns) headers.push("Video Title", "Channel");
    headers.push(showVideoColumns ? "URL" : "Web URL");
    if (showVideoColumns) headers.push("Download Status");
    if (showPlayVideo) headers.push("Play Video");
    headers.push("Ping Status", "Visited Date");
    if (isReplacedView) headers.push("Note");
    return headers;
  };

  const getRowData = (row) => {
    const data = [];
    if (showVideoColumns) data.push(row?.id || "");
    data.push(row?.course_id || "", row?.shortname || "", row?.section_name || "");
    if (showVideoColumns) data.push(row?.video_title || "", row?.channel_title || "");
    data.push(row?.video_url || "");
    if (showVideoColumns) data.push(row?.downloaded_status ? "Yes" : "No");
    if (showPlayVideo) data.push(row?.video_url ? "Available" : "");
    data.push(row?.url_status ? "Good" : "Bad", formatDate(row?.visited_updated));
    if (isReplacedView) data.push(row?.notes || "");
    return data;
  };

  // Add: helper to fetch export data (use page = 0 per request)
  const fetchExportData = async () => {
    const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
    const params = new URLSearchParams({
      page: 0, // request 0 to get all records (server-side behavior)
      per_page: 20,
      value: valueFromUrl,
      search: debouncedSearchText?.trim() ?? "",
    });

    try {
      const response = await axios.get(`${url}?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data.data || [];
    } catch (err) {
      console.error("Error fetching export data:", err);
      toast.error("Failed to fetch data for export");
      return [];
    }
  };

  const handleCopy = () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, copy: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : tableData;
    const rows = dataToExport.map((row) => getRowData(row));

    let textData = headers.join("\t") + "\n";
    rows.forEach((row) => {
      textData += row.join("\t") + "\n";
    });

    navigator.clipboard
      .writeText(textData)
      .then(() => {
        toast.success(`copied to clipboard!`);
      })
      .catch((err) => {
        toast.error("Failed to copy data");
        console.error("Copy error:", err);
      })
      .finally(() => {
        setButtonsLoading((s) => ({ ...s, copy: false, disabled: false }));
      });
  };

  const handleCSV = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, csv: true }));
    try {
      const headers = getTableHeaders();
      const dataToExport =
        selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : await fetchExportData();
      const rows = dataToExport.map((row) => getRowData(row));

      let csvContent = headers.map((h) => `"${h}"`).join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `pinging_report_${Date.now()}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV file downloaded successfully.");
    } catch (error) {
      toast.error("Failed to export CSV");
    } finally {
      setButtonsLoading((s) => ({ ...s, csv: false, disabled: false }));
    }
  };

  const handleExcel = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, excel: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : await fetchExportData();
    const rows = dataToExport.map((row) => getRowData(row));

    let excelContent =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>';

    excelContent += "<tr>";
    headers.forEach((header) => {
      excelContent += `<th style="background-color: #4472C4; color: white; font-weight: bold; border: 1px solid #000;">${header}</th>`;
    });
    excelContent += "</tr>";

    rows.forEach((row) => {
      excelContent += "<tr>";
      row.forEach((cell) => {
        excelContent += `<td style="border: 1px solid #000;">${cell}</td>`;
      });
      excelContent += "</tr>";
    });

    excelContent += "</table></body></html>";

    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `pinging_report_${Date.now()}.xls`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Excel file downloaded successfully.");
    setButtonsLoading((s) => ({ ...s, excel: false, disabled: false }));
  };

  const handlePDF = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, pdf: true }));

    try {
      const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
      const params = new URLSearchParams({
        page: 0, // request zero-based page so server will export full set
        per_page: 20,
        value: valueFromUrl,
        search: debouncedSearchText?.trim() ?? "",
        file_type: "pdf",
      });

      const resp = await axios.get(`${url}?${params.toString()}`, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });

      const data = resp?.data ?? {};
      if (data.success) {
        const msg = data.message || data.filename || "";
        if (!msg) {
          toast.error("Export returned no filename");
        } else {
          // message may be a full URL or just a filename — handle both
          const downloadUrl = msg.startsWith("http")
            ? msg
            : `${process.env.REACT_APP_S3VIDEO}${msg}`;

          // Step 1: Fetch file as blob → this guarantees download finished
          const fileResp = await axios.get(downloadUrl, { responseType: "blob" });

          const blobUrl = URL.createObjectURL(fileResp.data);

          // Step 2: Trigger browser download
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = (downloadUrl.split("/").pop() || "export.pdf").split("?")[0];
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(blobUrl);
          toast.success("PDF file downloaded successfully.");
        }
      } else {
        toast.error(data.message || "Failed to generate PDF on server");
      }
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to request PDF export");
    } finally {
      setButtonsLoading((s) => ({ ...s, pdf: false, disabled: false }));
    }
  };

  const handlePrint = () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, print: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : tableData;
    const rows = dataToExport.map((row) => getRowData(row));

    // Create a hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        @media print {
          @page {
            size: landscape;
            margin: 10mm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
        }
        body { 
          font-family: Arial, sans-serif; 
          margin: 20px;
          background-color: #ffffff;
        }
        h2 { 
          color: #333; 
          margin-bottom: 10px; 
        }
        p {
          margin-bottom: 20px;
        }
        table { 
          width: 100%; 
          border-collapse: collapse;
          table-layout: fixed;
        }
        th { 
          background-color: #4472C4 !important; 
          color: #ffffff !important; 
          padding: 8px; 
          text-align: left; 
          border: 1px solid #ddd; 
          font-weight: bold;
          font-size: 11px;
          word-wrap: break-word;
          white-space: normal;
        }
        td { 
          padding: 8px; 
          border: 1px solid #ddd;
          font-size: 10px;
          word-wrap: break-word;
          white-space: normal;
          overflow-wrap: break-word;
        }
        tr:nth-child(even) { 
          background-color: #f9f9f9; 
        }
      </style>
    </head>
    <body>
      <h2>${title}</h2>
      <p>Generated on: ${new Date().toLocaleString()}</p>
      <table>
        <thead>
          <tr>
            ${headers.map((h) => `<th>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${rows
        .map(
          (row, index) => `
            <tr>
              ${row.map((cell) => `<td>${cell}</td>`).join("")}
            </tr>
          `,
        )
        .join("")}
        </tbody>
      </table>
    </body>
    </html>
  `;

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    // Wait for content to load, then print and remove iframe
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after a short delay to ensure print dialog has opened
        setTimeout(() => {
          document.body.removeChild(iframe);
          setButtonsLoading((s) => ({ ...s, print: false, disabled: false }));
        }, 1000);
      }, 500);
    };
  };

  // Delete functionality
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
    setSelectedIds([item.id]);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    const remainingItemsAfterDelete = tableData.length - selectedIds.length;
    const shouldGoToPreviousPage = remainingItemsAfterDelete === 0 && page > 1;
    try {
      const deleteUrl = `${process.env.REACT_APP_LMS_URL}/webservice/rest/server.php`;
      const params = new URLSearchParams({
        wstoken: process.env.REACT_APP_LMS_TOKEN,
        wsfunction: "local_course_service_ping_delete",
        moodlewsrestformat: "json",
        id: itemToDelete.id,
        tbl: type,
      });
      const response = await axios.get(`https://${deleteUrl}?${params.toString()}`);
      if (response.data) {
        toast.success("Deleted successfully");
        setShowDeleteModal(false);
        setItemToDelete(null);
        setSelectedIds([]);

        // Reload the data
        if (shouldGoToPreviousPage) {
          setPage(page - 1);
        } else {
          loadItems();
        }
      } else {
        toast.error("Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Error deleting item");
      setSelectedIds([]);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setSelectedIds([]);
  };

  // Note functionality
  const handleAddNoteClick = (item) => {
    setItemToNote(item);
    setCurrentNote(item.notes || "");
    setShowNoteModal(true);
  };

  const handleNoteConfirm = async () => {
    if (!itemToNote) return;

    setNoteLoading(true);
    try {
      const noteUrl = `${process.env.REACT_APP_LMS_URL}/webservice/rest/server.php`;
      const params = new URLSearchParams({
        wstoken: process.env.REACT_APP_LMS_TOKEN,
        wsfunction: "local_course_service_ping_note",
        moodlewsrestformat: "json",
        id: itemToNote.id,
        note: currentNote,
        tbl: type,
      });
      const response = await axios.get(`${noteUrl}?${params.toString()}`);
      if (response.data) {
        toast.success("Note updated successfully");
        setShowNoteModal(false);
        setItemToNote(null);
        setCurrentNote("");
        loadItems();
      } else {
        toast.error("Failed to update note");
      }
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("Error updating note");
    } finally {
      setNoteLoading(false);
    }
  };

  const handleNoteClose = () => {
    setShowNoteModal(false);
    setItemToNote(null);
    setCurrentNote("");
  };

  // Strip stray quotes/whitespace from URLs returned by the API
  // e.g. "'https://lms.svhs.co/mod/'page/view.php?id=1" -> "https://lms.svhs.co/mod/page/view.php?id=1"
  const sanitizeUrl = (url) => {
    if (!url || typeof url !== "string") return url;
    // Remove literal and percent-encoded quotes (%27 = ', %22 = ") plus whitespace
    let cleaned = url.replace(/%27|%22/gi, "").replace(/['"`\s]/g, "");
    if (cleaned && !/^https?:\/\//i.test(cleaned)) {
      cleaned = cleaned.startsWith("//") ? `https:${cleaned}` : `https://${cleaned}`;
    }
    return cleaned;
  };

  const handleUrlClick = async (e, row, urlType) => {
    e.preventDefault();
    if (!urlType) return;

    let urlToOpen = sanitizeUrl(urlType === "video_url" ? row.video_url || row.web_url : row[urlType]);
    if (!urlToOpen) return;

    if (urlToOpen && urlToOpen.includes("youtube.com")) {
      urlToOpen = urlToOpen.replace("http://", "https://");
    }

    window.open(urlToOpen, "_blank", "noopener");
    if (urlType === "video_url") {
      // Call API in background
      try {
        const visitedUrl = `${process.env.REACT_APP_LMS_URL}/webservice/rest/server.php`;
        const params = new URLSearchParams({
          wstoken: process.env.REACT_APP_LMS_TOKEN,
          wsfunction: "local_course_service_ping_visited",
          moodlewsrestformat: "json",
          id: row.id,
          tbl: type,
        });
        const response = await axios.get(`${visitedUrl}?${params.toString()}`);
        if (response.data) {
          loadItems();
        }
      } catch (error) {
        console.error("Error recording visit:", error);
        toast.error("Failed to record visit");
      }
    }
  };

  const TableCellWithTooltip = ({ children, maxLength = 20, style = {}, ...rest }) => {
    const text = children?.toString() || "";
    const isOverflow = text.length > maxLength;

    const displayText = isOverflow ? text.substring(0, maxLength) + "..." : text;

    return (
      <td
        {...rest}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}
      >
        <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
          <span style={{ cursor: "default" }}>{displayText}</span>
        </OverlayTrigger>
      </td>
    );
  };

  const getColumnWidths = ({ pingType, showVideoColumns, showPlayVideo, isReplacedView }) => {
    if (pingType === "video") {
      return {
        select: "2%",
        id: showVideoColumns ? "4%" : "0%",
        courseId: "4%",
        courseName: "10%",
        pageTitle: "18%",
        videoTitle: showVideoColumns ? "17%" : "0%",
        channel: showVideoColumns ? "10%" : "0%",
        url: "18%",
        downloadStatus: showVideoColumns && !isReplacedView ? "6%" : "0%",
        playVideo: showPlayVideo ? "5%" : "0%",
        pingStatus: !isReplacedView ? "7%" : "0%",
        visitedDate: !isReplacedView ? "8%" : "0%",
        date: isReplacedView ? "8%" : "0%",
        note: isReplacedView ? "10%" : "0%",
        delete: "5%",
      };
    } else {
      // web table
      return {
        select: "2%",
        courseId: "8%",
        courseName: "15%",
        pageTitle: "25%",
        url: "25%",
        pingStatus: "10%",
        visitedDate: "10%",
        delete: "5%",
      };
    }
  };

  const widths = getColumnWidths({ pingType, showVideoColumns, showPlayVideo, isReplacedView });
  const calculateColspan = () => {
    let colspan = 7; // Base columns: checkbox, course id, course name, page title, url, ping status, visited date, delete
    if (showVideoColumns) colspan += 3; // ID, Video Title, Channel, Download Status
    if (showPlayVideo) colspan += 1; // Play Video
    if (isReplacedView) colspan += 1; // Note
    return colspan;
  };

    // ✅ PLACE SortableHeader HERE
  const SortableHeader = ({ label, column }) => {
    const isActive = sortBy === column;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => handleSort(column)}
      >
        <span>{label}</span>
        <img
          src="/images/sorting-icon.svg"
          alt="sort"
          style={{
            width: "12px",
            display: "inline-block", // prevents layout shift on rotation
            opacity: isActive ? 1 : 0.4,
            transform: isActive && sortOrder === "desc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease", // smooth rotation
          }}
        />
      </div>
    );
  };
  return (
    <Container fluid className="p-4">
      <Breadcrumbs />
      <p className="section-title mb-4">{getDisplayTitle()}</p>
      <Row className="align-items-end mb-3">
        <Col md="3">
          <Form.Group className="mb-2 text-start w-100">
            <Form.Label className="mb-1">Search</Form.Label>
            <div className="position-relative w-100">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                style={{
                  paddingRight: "35px",
                }}
              />
              {searchText.trim() && (
                <FaTimes
                  onClick={() => setSearchText("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: "16px",
                  }}
                />
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md="9" className="d-flex justify-content-end align-items-end">
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
            <Button size="sm" onClick={handleCopy} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.copy ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />Copy
                </>
              ) : (
                "Copy"
              )}
            </Button>
            <Button size="sm" onClick={handleCSV} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.csv ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                  CSV
                </>
              ) : (
                "CSV"
              )}
            </Button>
            <Button size="sm" onClick={handleExcel} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.excel ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  Excel
                </>
              ) : (
                "Excel"
              )}
            </Button>
            <Button size="sm" onClick={handlePDF} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.pdf ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  PDF
                </>
              ) : (
                "PDF"
              )}
            </Button>
            <Button size="sm" onClick={handlePrint} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.print ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  Print
                </>
              ) : (
                "Print"
              )}
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Table
          responsive
          className="scribe-contents-table"
          // style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead>
            <tr>
              {/* Checkbox column */}

              <th style={{ width: widths.select }}>
                <Form.Check
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length > 0 && selectedIds.length === tableData?.length}
                />
              </th>

              {/* Video-specific ID column */}
              {showVideoColumns && <th style={{ width: widths.id }}><SortableHeader label="ID" column="id" /></th>}

              {/* Common columns */}
              <th style={{ width: widths.courseId }}><SortableHeader label="Course ID" column="course_id" /></th>
              <th style={{ width: widths.courseName }}><SortableHeader label="Course Name" column="shortname" /></th>
              <th style={{ width: widths.pageTitle }}><SortableHeader label="Page Title" column="section_name" /></th>

              {/* Video-specific columns */}
              {showVideoColumns && <th style={{ width: widths.videoTitle }}><SortableHeader label="Video Title" column="video_title" /> </th>}
              {showVideoColumns && <th style={{ width: widths.channel }}><SortableHeader label="Channel" column="channel_title" /></th>}

              {/* URL column */}
              <th style={{ width: widths.url }}>
                <SortableHeader label={pingType === "video" ? (isReplacedView ? "Broken URL" : "URL") : "Web URL"} column="video_url" />
              </th>

              {/* Video-specific optional columns */}
              {showVideoColumns && !isReplacedView && (
                <th style={{ width: widths.downloadStatus }}><SortableHeader label="Download Status" column="downloaded_status" /></th>
              )}
              {showPlayVideo && (
                <th style={{ width: widths.playVideo }}>
                  <SortableHeader label={isReplacedView ? "Replaced Video" : "Play Video"} column="downloaded_status" />
                </th>
              )}

              {/* Common ping/date columns */}
              {!isReplacedView && <th style={{ width: widths.pingStatus }}><SortableHeader label="Ping Status" column="url_status" /></th>}
              <th
                style={{
                  width: pingType === "video" && isReplacedView ? widths.date : widths.visitedDate,
                }}
              >
                <SortableHeader label={isReplacedView && pingType === "video" ? "Date" : "Visited Date"} column={isReplacedView && pingType === "video" ? "date" : "visited_updated"} />
              </th>

              {/* Notes column */}
              {pingType === "video" && isReplacedView && (
                <th style={{ width: widths.note }}><SortableHeader label="Note" column="notes" /></th>
              )}

              {/* Delete column */}
              {(isAdmin || hasEdit(toolId)) &&
              <th style={{ width: widths.delete }}>Delete</th>
              }
            </tr>
          </thead>
          <tbody>
            {showSpinner ? (
              <tr>
                <td
                  colSpan={calculateColspan()}
                  style={{ textAlign: "center", padding: "4rem 0", height: "30px" }}
                >
                  <Button
                    variant="link"
                    className="mt-2 mb-5 btn-spinner ms-0 text-decoration-none"
                    disabled
                  >
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                  </Button>
                </td>
              </tr>
            ) : tableData?.length > 0 ? (
              tableData.map((row) => (
                <tr key={row?.id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.includes(row?.id)}
                      onChange={() => handleSelectRow(row?.id)}
                    />
                  </td>
                  {showVideoColumns && <td>{row?.id}</td>}
                  <TableCellWithTooltip maxLength={10}>{row?.course_id}</TableCellWithTooltip>
                  <TableCellWithTooltip maxLength={15}>{row?.shortname}</TableCellWithTooltip>

                  <td>
                    {row?.page_url ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-${row.id}`}>{row.section_name}</Tooltip>}
                      >
                        <a
                          href={sanitizeUrl(row.page_url)}
                          onClick={(e) => handleUrlClick(e, row, "page_url")}
                          style={{
                            cursor: "pointer",
                            color: row?.visited_updated && pingType === "web" ? "" : "#007bff",
                            textDecoration: "underline",
                          }}
                          target="_blank"
                          rel="noopener"
                          referrerPolicy="strict-origin-when-cross-origin"
                        >
                          {row.section_name.length > (pingType === "web" ? 40 : 28)
                            ? row.section_name.substring(0, pingType === "web" ? 40 : 28) + "..."
                            : row.section_name}
                        </a>
                      </OverlayTrigger>
                    ) : (
                      ""
                    )}
                  </td>

                  {showVideoColumns && (
                    <>
                      {showVideoColumns && (
                        <TableCellWithTooltip>{row?.video_title}</TableCellWithTooltip>
                      )}
                      {showVideoColumns && (
                        <TableCellWithTooltip>{row?.channel_title}</TableCellWithTooltip>
                      )}
                    </>
                  )}
                  <td>
                    {row?.video_url || row?.web_url ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-url-${row.id}`}>
                            {row.video_url || row.web_url}
                          </Tooltip>
                        }
                      >
                        <a
                          href={sanitizeUrl(row.video_url || row.web_url)}
                          onClick={(e) => handleUrlClick(e, row, "video_url")}
                          style={{
                            cursor: "pointer",
                            color: row?.visited_updated && pingType === "web" ? "red" : "#007bff",
                            textDecoration: "underline",
                          }}
                          target="_blank"
                          rel="noopener"
                          referrerPolicy="strict-origin-when-cross-origin"
                        >
                          {(row.video_url || row.web_url).length > (pingType === "web" ? 40 : 25)
                            ? (row.video_url || row.web_url).substring(
                                0,
                                pingType === "web" ? 40 : 25,
                              ) + "..."
                            : row.video_url || row.web_url}
                        </a>
                      </OverlayTrigger>
                    ) : (
                      ""
                    )}
                  </td>

                  {showVideoColumns && !isReplacedView && (
                    <td style={{ textAlign: "center" }}>
                      {row?.downloaded_status || row?.downloaded_url_id ? (
                        <FaCheck color="green" />
                      ) : (
                        <FaTimes color="red" />
                      )}
                    </td>
                  )}
                  {showPlayVideo && (
                    <td>
                      {row?.video_url && (row?.downloaded_status || row?.downloaded_url_id) ? (
                        <img
                          onClick={() => handlePlayVideo(row?.downloaded_url_id)}
                          style={{
                            cursor: "pointer",
                            color: "#007bff",
                            textDecoration: "underline",
                            textAlign: "center",
                          }}
                          src={"/images/play-circle.svg"}
                          alt="play"
                        ></img>
                      ) : (
                        <span style={{ color: "#999" }}></span>
                      )}
                    </td>
                  )}
                  {!isReplacedView && (
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {row?.url_status ? (
                          <>
                            <FaCheck color="green" />
                            <span style={{ color: "green" }}>Good</span>
                          </>
                        ) : (
                          <>
                            <FaTimes color="red" />
                            <span style={{ color: "red" }}>Bad</span>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                  <td>{formatDate(row?.visited_updated)}</td>

                  {isReplacedView && (
                    <td style={{ textAlign: !row?.notes && "center" }}>
                      {row?.notes ? (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`tooltip-${row.id}`}>{row?.notes}</Tooltip>}
                          >
                            <span
                              style={{
                                flex: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100px", // adjust width for truncation
                                minWidth: 0,
                                lineHeight: "1.5",
                                cursor: "default",
                              }}
                            >
                              {row?.notes}
                            </span>
                          </OverlayTrigger>

                          <img
                            style={{
                              cursor: "pointer",
                              width: "16px",
                              height: "16px",
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                            src={"/images/edit.svg"}
                            alt="edit"
                            onClick={() => handleAddNoteClick(row)}
                            title="Edit Note"
                          />
                        </div>
                      ) : (
                        <img
                          onClick={() => handleAddNoteClick(row)}
                          src={"/images/plus-circle-grey.svg"}
                          style={{
                            textAlign: "center",
                            cursor: "pointer",
                            color: "#6B6B6B",
                          }}
                          alt="add"
                          title="Add Note"
                        />
                      )}
                    </td>
                  )}

                  {(isAdmin || hasEdit(toolId)) &&
                  <td>
                    <div className="d-flex justify-content-end">
                      <img
                        style={{ cursor: "pointer" }}
                        src={"/images/delete-grey.svg"}
                        alt="delete"
                        onClick={() => handleDeleteClick(row)}
                      />
                    </div>
                  </td>
                  }
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={calculateColspan()} style={{ textAlign: "center", padding: "2rem" }}>
                  <img
                    src="/images/no-data-found.jpeg"
                    style={{ maxWidth: "400px" }}
                    alt="No data"
                  />
                  <p>No data found...</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Row>

      {tableData?.length > 0 && !showSpinner && (
        <Row>
          <Col className="d-flex justify-content-start disabled">
            Displaying {tableData.length} of {paginationData.total_records} Records
          </Col>
          <Col className="d-flex justify-content-end">
            <Paginations
              current_page={paginationData.current_page}
              page_size={paginationData.page_size}
              total_page={paginationData.total_page}
              total_records={paginationData.total_records}
              clicked_page={handlePageChange}
            />
          </Col>
        </Row>
      )}

      {showVideoModal && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    }}
    onClick={() => setShowVideoModal(false)}
  >
    <div
      style={{
        position: "relative",
        width: "90%",
        maxWidth: "800px",
        backgroundColor: "#000",
        padding: "10px",
        borderRadius: "10px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setShowVideoModal(false)}
        style={{
          position: "absolute",
          top: "-10px",
          right: "-10px",
          background: "#fff",
          border: "none",
          borderRadius: "50%",
          width: "30px",
          height: "30px",
          cursor: "pointer",
          fontSize: "18px",
          zIndex: 10000,
        }}
      >
        ×
      </button>

      <video
        width="100%"
        height="450"
        controls
        autoPlay
        style={{ borderRadius: "8px" }}
      >
        <source src={currentVideoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>
)}

      {showDeleteModal && (
        <DeleteConfirmationModal
          loading={deleteLoading}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          description="Are you sure you want to delete this record? This action cannot be undone."
        />
      )}

      {showNoteModal && (
        <Modal show={showNoteModal} onHide={handleNoteClose} centered backdrop="static" size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{itemToNote?.note ? "Edit Note" : "Add Note"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                placeholder="Enter your note here..."
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleNoteClose} disabled={noteLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleNoteConfirm} disabled={noteLoading}>
              {noteLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-1"
                  />
                  Saving...
                </>
              ) : (
                "Save Note"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default PingingDataTable;
